import { type ColumnDef, getCoreRowModel } from '@tanstack/react-table'
import { useId, useMemo } from 'react'
import { useDataTable } from '../../hooks/use-data-table'
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../ui/accordion'
import { Alert, AlertDescription } from '../ui/alert'
import { DataTable } from '../ui/data-table'
import { Tag } from '../ui/tag'
import { ImportProblems } from './import-problems'

/**
 * The shape an import report arrives in. It mirrors the API's report payload, so
 * a caller passes the response as it is; the API has already translated every
 * label and message, and values never appear here.
 */
export type ImportReportSection = {
  key: string
  label: string
  created: number
  updated: number
  unchanged: number
  errors: number
}

export type ImportReportError = {
  section: string
  section_label: string
  row: number
  column: string | null
  column_label: string | null
  message: string
}

export type ImportReportChange = {
  section: string
  row: number
  label: string
  fields: unknown[]
}

/** One money total of a section, in one currency; the amount is a decimal string. */
export type ImportReportTotal = {
  section: string
  section_label: string
  label: string
  amount: string
  currency: string
}

export type ImportReportData = {
  file_error: string | null
  error_total: number
  sections: ImportReportSection[]
  errors: ImportReportError[]
  /** Findings the rows import with, such as a value left out. Same shape as `errors`. */
  warnings: ImportReportError[]
  changes: ImportReportChange[]
  /** Money totals a section reports, such as the amount still owed on imported advances. */
  totals?: ImportReportTotal[]
}

export type ImportReportLabels = {
  summary: string
  sheet: string
  created: string
  updated: string
  unchanged: string
  errors: string
  problems: string
  row: string
  column: string
  problem: string
  wholeRow: string
  warnings: string
  warning: string
  warningsHint: string
  changes: string
  changesHint: string
  totals: string
}

export type ImportReportProps = {
  report: ImportReportData
  labels: ImportReportLabels
  /** "Showing the first {shown} of {total} problems", written by the caller. */
  formatProblemsMore: (shown: number, total: number) => string
  /** Formats a total's decimal amount with its currency, in the reader's locale. */
  formatAmount: (amount: string, currency: string) => string
}

/**
 * An import report: a summary per sheet, the problems to fix, the warnings the
 * rows import with, and which fields each row changes. A file that cannot be
 * read shows its message alone.
 */
export function ImportReport({
  report,
  labels,
  formatProblemsMore,
  formatAmount,
}: ImportReportProps) {
  const id = useId()
  const summaryColumns = useMemo<ColumnDef<ImportReportSection>[]>(
    () => [
      {
        id: 'sheet',
        header: labels.sheet,
        cell: ({ row }) => (
          <span className="whitespace-normal font-medium">{row.original.label}</span>
        ),
        meta: { phone: 'primary' },
      },
      {
        id: 'created',
        header: labels.created,
        cell: ({ row }) => figure(row.original.created),
        meta: { numeric: true, phone: 'meta', phoneLabel: labels.created },
      },
      {
        id: 'updated',
        header: labels.updated,
        cell: ({ row }) => figure(row.original.updated),
        meta: { numeric: true, phone: 'meta', phoneLabel: labels.updated },
      },
      {
        id: 'unchanged',
        header: labels.unchanged,
        cell: ({ row }) => figure(row.original.unchanged, 'muted'),
        meta: { numeric: true, phone: 'meta', phoneLabel: labels.unchanged },
      },
      {
        id: 'errors',
        header: labels.errors,
        cell: ({ row }) => figure(row.original.errors, 'danger'),
        meta: { numeric: true, phone: 'trailing', phoneLabel: labels.errors },
      },
    ],
    [labels],
  )
  const summaryTable = useDataTable({
    data: report.sections,
    columns: summaryColumns,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) => row.key,
  })

  if (report.file_error !== null) {
    return (
      <Alert severity="danger" data-testid="data-import-file-error">
        <AlertDescription>{report.file_error}</AlertDescription>
      </Alert>
    )
  }

  return (
    <div className="flex flex-col gap-5">
      <section aria-labelledby={`${id}-summary`}>
        <h3 id={`${id}-summary`} className="mb-2 text-label text-muted-foreground">
          {labels.summary}
        </h3>
        <DataTable
          table={summaryTable}
          phoneLayout="rows"
          caption={labels.summary}
          data-testid="data-import-summary"
        />
      </section>

      {(report.totals ?? []).length > 0 ? (
        <section aria-labelledby={`${id}-totals`}>
          <h3 id={`${id}-totals`} className="mb-2 text-label text-muted-foreground">
            {labels.totals}
          </h3>
          <dl className="flex flex-col divide-y divide-border" data-testid="data-import-totals">
            {(report.totals ?? []).map((total) => (
              <div
                key={`${total.section}-${total.label}-${total.currency}`}
                className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 py-2"
              >
                <dt className="text-body">
                  {total.section_label}
                  <span className="text-muted-foreground"> · {total.label}</span>
                </dt>
                <dd className="font-medium tabular-nums">
                  {formatAmount(total.amount, total.currency)}
                </dd>
              </div>
            ))}
          </dl>
        </section>
      ) : null}

      {report.errors.length > 0 ? (
        <ImportProblems
          rows={report.errors}
          tone="danger"
          heading={labels.problems}
          messageHeader={labels.problem}
          labels={labels}
          note={
            report.error_total > report.errors.length
              ? formatProblemsMore(report.errors.length, report.error_total)
              : undefined
          }
          data-testid="data-import-problems"
        />
      ) : null}

      {report.warnings.length > 0 ? (
        <ImportProblems
          rows={report.warnings}
          tone="warning"
          heading={labels.warnings}
          messageHeader={labels.warning}
          labels={labels}
          note={labels.warningsHint}
          count={report.warnings.length}
          data-testid="data-import-warnings"
        />
      ) : null}

      {report.changes.length > 0 ? <ImportChanges report={report} labels={labels} /> : null}
    </div>
  )
}

/** A figure in a summary cell: a problem count reads in danger, and a zero reads quietly. */
function figure(value: number, tone?: 'danger' | 'muted') {
  const className =
    tone === 'danger' && value > 0
      ? 'font-medium text-danger'
      : tone === 'muted' || value === 0
        ? 'text-muted-foreground'
        : undefined
  return <span className={className}>{value}</span>
}

function ImportChanges({
  report,
  labels,
}: {
  report: ImportReportData
  labels: ImportReportLabels
}) {
  const id = useId()
  const bySection = report.sections
    .map((section) => ({
      section,
      rows: report.changes.filter((change) => change.section === section.key),
    }))
    .filter((group) => group.rows.length > 0)

  return (
    <section aria-labelledby={id}>
      <h3 id={id} className="text-label text-muted-foreground">
        {labels.changes}
      </h3>
      <p className="mb-1 text-small text-muted-foreground">{labels.changesHint}</p>
      <Accordion type="multiple" data-testid="data-import-changes">
        {bySection.map(({ section, rows }) => (
          <AccordionItem key={section.key} value={section.key}>
            <AccordionTrigger>
              <span>
                {section.label}
                <span className="ms-2 text-muted-foreground tabular-nums">{rows.length}</span>
              </span>
            </AccordionTrigger>
            <AccordionContent>
              <ul className="flex flex-col divide-y divide-border">
                {rows.map((row) => (
                  <li
                    key={`${row.row}-${row.label}`}
                    className="flex flex-wrap items-baseline gap-x-3 gap-y-1 py-2"
                  >
                    <span className="w-16 shrink-0 text-small text-muted-foreground tabular-nums">
                      {labels.row} {row.row}
                    </span>
                    <span className="font-mono text-identifier">{row.label}</span>
                    <span className="flex flex-wrap gap-1">
                      {row.fields.map(String).map((field) => (
                        <Tag key={field}>{field}</Tag>
                      ))}
                    </span>
                  </li>
                ))}
              </ul>
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </section>
  )
}
